/**
 * Reliable source for the user's `backup` config in the ENGINE (content) context.
 *
 * The scheduler resolves config from the Redux store (`selectUser`). But the
 * extension/engine content store doesn't always carry the freshly-patched
 * `backup` sub-doc (extension-vs-web store drift — see the memory note
 * "WA tab store missing reducers"). When it doesn't, `dbBackupEnabled` defaults
 * to false and the backup silently never runs on the engine — even for users
 * who enabled it.
 *
 * This module fetches `backup` straight from the backend (authenticated
 * `/users/:id`) and caches it, so the effective-config resolution has a
 * store-independent fallback. Cache also survives reloads via localStorage.
 */
import { apiCall } from '@extension/wa-communication-layer/api/api-call-helper';
import type { RawBackupConfig } from './backup-config';

const CACHE_KEY = 'bt-backup:cfg-cache';

let memoryCache: RawBackupConfig | null = null;

/** Read the last backend-fetched backup config (memory → localStorage). */
export const getCachedBackendBackup = (): RawBackupConfig | null => {
  if (memoryCache) return memoryCache;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) { memoryCache = JSON.parse(raw) as RawBackupConfig; return memoryCache; }
  } catch { /* ignore */ }
  return null;
};

const setCache = (backup: RawBackupConfig | null): void => {
  memoryCache = backup;
  try {
    if (backup) localStorage.setItem(CACHE_KEY, JSON.stringify(backup));
  } catch { /* ignore */ }
};

/** base64url → JSON. JWT segments are base64url (`-`/`_`, no padding), which
 *  `atob` alone can't decode — normalize + pad first. */
const decodeJwtPayload = (token: string): any | null => {
  try {
    const part = token.split('.')[1] || '';
    if (!part) return null;
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
};

/** Resolve the current user's id from the bt-token JWT (store-independent). */
const userIdFromToken = (): string | null => {
  const payload = decodeJwtPayload(localStorage.getItem('bt-token') || '');
  return payload?.sub || payload?.userId || payload?._id || null;
};

/**
 * Fetch `backup` from the backend and cache it. Best-effort — returns the
 * cached value (or null) on any failure so the caller degrades gracefully.
 * `userId` falls back to the JWT subject when not supplied by the store.
 */
export const refreshBackendBackupConfig = async (
  userId?: string | null,
): Promise<RawBackupConfig | null> => {
  const id = userId || userIdFromToken();
  if (!id) return getCachedBackendBackup();
  try {
    const user = await apiCall<{ backup?: RawBackupConfig }>('get', `/users/${id}`);
    const backup = user?.backup ?? null;
    setCache(backup);
    return backup;
  } catch {
    return getCachedBackendBackup();
  }
};
